import { z } from 'zod';
import { createPaginatedResponseSchema } from './common.js';
// =============================================================================
// Supplier Invoice Detection (admin read-only viewer)
// =============================================================================
/**
 * Coarse document classification produced by the pre-extraction email scorer.
 * Deliberately separate from SupplierInvoiceDocumentType (the post-extraction
 * vision-LLM verdict): RECEIPT and STATEMENT are meaningful at detection time
 * but would be misleading on a completed extraction row.
 */
export const SupplierInvoiceDetectionDocTypeSchema = z.enum([
    'INVOICE',
    'RECEIPT',
    'STATEMENT',
    'NOT_INVOICE',
    'UNCERTAIN',
]);
/**
 * Confidence in the scorer's verdict. `none` is only emitted when the email
 * failed a hard gate (wrong direction or content-filtered); all non-gated
 * emails receive at least `low`.
 */
export const SupplierInvoiceDetectionConfidenceSchema = z.enum(['high', 'medium', 'low', 'none']);
// =============================================================================
// Admin VIEW schemas (read-only; no label/training fields)
// =============================================================================
/**
 * Query-string schema for the admin list endpoint.
 * - `from`/`to`: ISO datetime range (required; server applies as `createdAt` bounds).
 * - `accountId`: restrict to a single account.
 * - `bucket`: filter by confidence bucket (`high`/`medium`/`low`/`none`).
 * - `isExpenseCandidate`: tri-state boolean filter. Pass `'true'` for matched rows only;
 *   `'false'` for non-matched only; omit to include both. Uses string enum to parse
 *   query-string values correctly (`'false'` must not coerce to `true`).
 * - `cursor`: opaque keyset cursor returned by the previous page (`nextCursor`).
 * - `limit`: page size; defaults to 50, capped at 100.
 */
export const AdminSupplierInvoiceDetectionQuerySchema = z.object({
    from: z.iso.datetime({ error: 'from must be a valid ISO datetime string' }),
    to: z.iso.datetime({ error: 'to must be a valid ISO datetime string' }),
    accountId: z.uuid().optional(),
    bucket: SupplierInvoiceDetectionConfidenceSchema.optional(),
    isExpenseCandidate: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
});
/**
 * One row in the admin list. Contains only scorer metadata; no decrypted email
 * content and no label field.
 */
export const AdminSupplierInvoiceDetectionSummarySchema = z.object({
    id: z.string(),
    storedEmailId: z.string(),
    accountId: z.string(),
    algorithmVersion: z.string(),
    isExpenseCandidate: z.boolean(),
    documentTypeGuess: SupplierInvoiceDetectionDocTypeSchema,
    score: z.number(),
    confidence: SupplierInvoiceDetectionConfidenceSchema,
    gatedReason: z.string().nullable(),
    createdAt: z.string(),
});
export const AdminSupplierInvoiceDetectionListResponseSchema = createPaginatedResponseSchema(AdminSupplierInvoiceDetectionSummarySchema);
/**
 * Path params for the detail and body endpoints.
 */
export const AdminSupplierInvoiceDetectionParamsSchema = z.object({
    storedEmailId: z.uuid(),
});
/**
 * One signal entry in a detection record. `code` is the stable scorer signal
 * identifier (e.g. `ATTACHMENT_PDF`), not raw text (no PII).
 */
export const AdminSupplierInvoiceDetectionSignalSchema = z.object({
    code: z.string(),
    weight: z.number(),
    matched: z.boolean(),
});
/**
 * One detection version within the detail response.
 */
export const AdminSupplierInvoiceDetectionVersionSchema = z.object({
    id: z.string(),
    algorithmVersion: z.string(),
    isExpenseCandidate: z.boolean(),
    documentTypeGuess: SupplierInvoiceDetectionDocTypeSchema,
    score: z.number(),
    confidence: SupplierInvoiceDetectionConfidenceSchema,
    gatedReason: z.string().nullable(),
    signals: z.array(AdminSupplierInvoiceDetectionSignalSchema),
    createdAt: z.string(),
});
/**
 * One attachment entry in the detection detail response. Metadata only –
 * file bytes are never fetched or returned. `filename` is decrypted by the
 * service before including it here. Only present on the detail view; the list
 * summary carries no attachment data.
 */
export const AdminSupplierInvoiceDetectionAttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
});
/**
 * Full detail for a stored email: all detection versions plus decrypted header
 * fields (subject, snippet, from, to) and attachment metadata. No label field.
 *
 * `attachments` is an empty array when the email had no attachments. Each
 * entry carries a decrypted `filename`, plaintext `mimeType`, and `sizeBytes`.
 * The overall detail response is super-admin-gated (same as subject/from/to),
 * so surfacing decrypted filenames here is consistent with the existing
 * security boundary.
 */
export const AdminSupplierInvoiceDetectionDetailSchema = z.object({
    storedEmailId: z.string(),
    accountId: z.string(),
    detections: z.array(AdminSupplierInvoiceDetectionVersionSchema),
    subject: z.string(),
    snippet: z.string(),
    from: z.object({
        name: z.string().nullable(),
        email: z.string(),
    }),
    to: z.array(z.object({
        name: z.string().nullable(),
        email: z.string(),
    })),
    attachments: z.array(AdminSupplierInvoiceDetectionAttachmentSchema),
});
/**
 * Response for the on-demand body endpoint.
 * `body` is the decrypted plain-text email body; may be empty when the stored
 * email carried no body.
 */
export const AdminSupplierInvoiceDetectionBodyResponseSchema = z.object({
    storedEmailId: z.string(),
    body: z.string(),
});
